import React, { FC, useEffect, useState, useMemo } from 'react';
import { FormControl, TextField, Autocomplete, FormHelperText } from '@mui/material';
import { useQuery } from 'react-query';
import { getSalesLookupTerritories } from '../../fetch/lookups/sales-portal';
import { IDropdownResponse } from '../../models';

interface ISalesTerritoryAutoCompleteProps {
  multiple?: boolean;
  value: string | number | (string | number)[];
  handleChange: (val: string | number | (string | number)[]) => void;
  isDisabled?: boolean;
  setOptions?: (val: IDropdownResponse[]) => void;
  isRequired?: boolean;
  message?: string;
  hasError?: boolean;
  handleClear?: () => void;
  apiParams?: any;
  limitTags?: number;
  disabled?: boolean;
}

export const SalesTerritoryAutoComplete: FC<ISalesTerritoryAutoCompleteProps> = ({
  disabled = false,
  isRequired,
  value,
  handleChange,
  setOptions,
  message,
  hasError,
  multiple = false,
  handleClear,
  apiParams,
  limitTags
}) => {
  const { isLoading: isLoadingTerritories, data: territoriesData } = useQuery<
    IDropdownResponse[],
    Error
  >(['territories-filters', apiParams], () => getSalesLookupTerritories(apiParams), {
    notifyOnChangeProps: 'tracked',
    onSuccess: d => setOptions?.(d)
  });

  const [options, setOptionsState] = useState<IDropdownResponse[]>([]);

  useEffect(() => {
    if (territoriesData) {
      setOptionsState(territoriesData);
    }
  }, [territoriesData]);

  const normalizedValue = useMemo(() => {
    return Array.isArray(value) ? value : value ? [value] : [];
  }, [value]);

  const selectedOptions = useMemo(() => {
    return options
      .map(territory => ({
        value: territory.value,
        label: territory.description
      }))
      .filter(option => normalizedValue.includes(option.value));
  }, [normalizedValue, options]);

  const filteredOptions = useMemo(() => {
    return options
      .map(territory => ({
        value: territory.value,
        label: territory.description
      }))
      .filter(option => !normalizedValue.includes(option.value));
  }, [options, normalizedValue]);

  return (
    <FormControl fullWidth variant="outlined" size="small" required={isRequired} error={hasError}>
      <Autocomplete
        size="small"
        limitTags={limitTags}
        multiple={multiple}
        disabled={disabled}
        disableCloseOnSelect={multiple}
        options={filteredOptions}
        getOptionLabel={option => option.label}
        value={selectedOptions}
        onChange={(event, newValue) => {
          if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
            handleClear?.();
            handleChange([]);
          } else if (Array.isArray(newValue)) {
            handleChange(
              newValue.map(option => {
                if (typeof option === 'string' || typeof option === 'number') {
                  return option;
                } else {
                  return option.value;
                }
              })
            );
          } else {
            handleChange(
              typeof newValue === 'string' || typeof newValue === 'number'
                ? newValue
                : newValue.value
            );
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            label={isLoadingTerritories ? 'Loading...' : 'Territories'}
            required={isRequired}
            InputProps={{
              ...params.InputProps,
              endAdornment: <>{params.InputProps.endAdornment}</>
            }}
          />
        )}
      />
      {message && <FormHelperText error>{message}</FormHelperText>}
    </FormControl>
  );
};
